import type { BomSummary } from '@erp/shared';
import { ListChecks } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Badge, Card, Skeleton } from '../../components/shadcn/index.js';
import { DataTable, type DataTableColumn, EmptyState } from '../../components/ui/index.js';
import { api } from '../../lib/api.js';
import { formatDateTime } from '../../lib/intl.js';
import { trimMoney } from '../../lib/money.js';

/**
 * Read-only index of every product that has a recipe. The EDITOR is the
 * product's own Recipe tab — one editor for one entity — so a row click (and
 * the empty state) deep-link there rather than duplicating the grid here.
 */
export function BomsPage(): JSX.Element {
  const { t, i18n } = useTranslation('manufacturing');
  const navigate = useNavigate();
  const [rows, setRows] = useState<BomSummary[] | null>(null);

  useEffect(() => {
    void api().get<{ items: BomSummary[] }>('/manufacturing/boms')
      .then((r) => setRows(r.data.items))
      .catch(() => setRows([]));
  }, []);

  if (!rows) return <Card className="p-6"><Skeleton className="h-32 w-full" /></Card>;

  const columns: DataTableColumn<BomSummary>[] = [
    {
      id: 'item',
      header: t('boms.product', { defaultValue: 'Product' }),
      cell: (r) => (
        <span>
          <span className="font-mono text-xs text-fg-muted">{r.itemCode}</span> · <span className="font-medium">{r.itemName}</span>
        </span>
      ),
      sortValue: (r) => r.itemCode,
      searchValue: (r) => `${r.itemCode} ${r.itemName}`,
    },
    {
      id: 'components',
      header: t('boms.components', { defaultValue: 'Components' }),
      align: 'end',
      cell: (r) => r.componentCount,
      sortValue: (r) => r.componentCount,
    },
    {
      id: 'output',
      header: t('boms.outputQty', { defaultValue: 'Batch yield' }),
      align: 'end',
      cell: (r) => <span className="font-mono">{trimMoney(r.outputQuantity)}</span>,
      sortValue: (r) => Number(r.outputQuantity),
    },
    {
      id: 'active',
      header: t('boms.active', { defaultValue: 'Active' }),
      cell: (r) => <Badge variant={r.isActive ? 'success' : 'secondary'}>{r.isActive ? t('boms.activeYes', { defaultValue: 'Active' }) : t('boms.activeNo', { defaultValue: 'Off' })}</Badge>,
      sortValue: (r) => (r.isActive ? 1 : 0),
    },
    {
      id: 'updatedAt',
      header: t('boms.updated', { defaultValue: 'Updated' }),
      cell: (r) => formatDateTime(r.updatedAt, { locale: i18n.language }),
      sortValue: (r) => r.updatedAt,
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="flex items-center gap-2 text-2xl font-semibold">
          <ListChecks className="h-6 w-6 text-brand" aria-hidden />
          {t('boms.pageTitle', { defaultValue: 'Recipes' })}
        </h1>
        <p className="mt-1 text-sm text-fg-muted">
          {t('boms.pageSubtitle', { defaultValue: 'What each product is made of. A recipe is edited on the product’s own Recipe tab.' })}
        </p>
      </header>

      {rows.length === 0 ? (
        <EmptyState
          icon={<ListChecks className="h-8 w-8" aria-hidden />}
          title={t('boms.pageEmpty', { defaultValue: 'No recipes yet' })}
          description={t('boms.pageEmptyHint', { defaultValue: 'Open a product → Recipe tab and add its components. It appears here automatically.' })}
        />
      ) : (
        <Card className="overflow-hidden p-0">
          <DataTable
            data={rows}
            columns={columns}
            rowKey={(r) => r.id}
            searchable
            onRowClick={(r) => navigate(`/inventory/items/${r.itemId}/edit?tab=recipe`)}
            initialSort={{ id: 'updatedAt', dir: 'desc' }}
          />
        </Card>
      )}
    </div>
  );
}
